import { Lock, Leaf, Sparkles, Star, Music2, Sun, Waves, ShieldCheck, Eye, AlertTriangle } from "lucide-react";
import { orNA, fmtDate, fmtMoney, fmtCredits, equivalent } from "@/lib/format";
import { XcaretCheckout } from "@/components/XcaretCheckout";

const rgba = (hex, a) => {
  const h = (hex || "#000000").replace("#", "");
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
};

const THEMES = {
  nickelodeon: { heading: "'Fredoka', sans-serif", body: "'Nunito', sans-serif", radius: 28, icon: Sparkles, tagline: "Tu aventura familiar está casi lista", border: 3, shadow: true },
  "hard-rock": { heading: "'Barlow Condensed', sans-serif", body: "'Outfit', sans-serif", radius: 6, dark: true, upper: true, icon: Music2, tagline: "Tu estancia, a todo volumen", border: 1 },
  barcelo: { heading: "'Playfair Display', serif", body: "'Plus Jakarta Sans', sans-serif", radius: 4, icon: Star, tagline: "Una estancia elegante te espera", border: 1, shadow: true, rule: true },
  "ava-resort": { heading: "'Outfit', sans-serif", body: "'Outfit', sans-serif", radius: 2, icon: Waves, tagline: "Calma frente al Caribe", border: 1, light: true },
  xcaret: { heading: "'Manrope', sans-serif", body: "'Manrope', sans-serif", radius: 20, icon: Leaf, tagline: "Naturaleza, cultura y descanso", border: 1, shadow: true },
  riu: { heading: "'Outfit', sans-serif", body: "'Work Sans', sans-serif", radius: 10, icon: Sun, tagline: "Todo listo para tu estancia", border: 1, shadow: true, redHeader: true },
};

const Row = ({ label, value, c, testid }) => (
  <div className="flex items-start justify-between gap-4 py-3" style={{ borderBottom: `1px solid ${c.line}` }}>
    <span className="text-sm" style={{ color: c.muted }}>{label}</span>
    <span className="text-right text-sm font-semibold" data-testid={testid}>{value}</span>
  </div>
);

const Guests = ({ d, c, t }) => (
  <div className="grid grid-cols-3 gap-2 py-4 sm:gap-3">
    {[["Adultos", d.adults, "adults"], ["Menores", d.minors, "minors"], ["Infantes", d.infants, "infants"]].map(([l, v, k]) => (
      <div key={k} className="px-2 py-3 text-center" style={{ background: c.soft, borderRadius: Math.min(t.radius, 16) }}>
        <p className={`font-bold ${v === null || v === undefined ? "text-xs leading-tight sm:text-sm" : "text-xl"}`} style={{ fontFamily: t.heading }} data-testid={`pay-${k}`}>{orNA(v)}</p>
        <p className="mt-1 text-xs" style={{ color: c.muted }}>{l}</p>
      </div>
    ))}
  </div>
);

const Header = ({ hotel, c, t }) => (
  <header className="px-5 py-4 sm:px-10" style={{ background: t.redHeader ? hotel.primary_color : "transparent", color: t.redHeader ? "#fff" : c.text }}>
    <div className="mx-auto flex max-w-6xl items-center justify-between">
      {hotel.logo_data
        ? <img src={hotel.logo_data} alt={hotel.display_name} className="h-10 max-w-[180px] object-contain" data-testid="pay-hotel-logo" />
        : <span className="text-xl font-bold sm:text-2xl" data-testid="pay-hotel-name"
            style={{ fontFamily: t.heading, color: t.redHeader ? "#fff" : hotel.primary_color, textTransform: t.upper ? "uppercase" : "none", letterSpacing: t.upper ? "0.08em" : t.light ? "0.04em" : 0 }}>
            {hotel.display_name}
          </span>}
      <span className="flex items-center gap-1.5 text-xs opacity-80"><Lock size={14} /> Pago seguro</span>
    </div>
  </header>
);

const Hero = ({ hotel, c, t }) => {
  const Icon = t.icon;
  return (
    <section className="relative h-48 overflow-hidden sm:h-64">
      <img src={`/hotels/${hotel.slug}.jpg`} alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${rgba(hotel.secondary_color, t.dark ? 0.55 : 0.25)} 0%, ${rgba(hotel.background_color, t.dark ? 0.85 : 0.55)} 65%, ${hotel.background_color} 100%)` }} />
      <div className="relative mx-auto flex h-full max-w-6xl flex-col justify-end px-5 pb-14 sm:px-10">
        <span className="mb-2 inline-flex w-fit items-center gap-1.5 px-3 py-1 text-xs font-semibold"
          style={{ background: hotel.accent_color, color: "#fff", borderRadius: 999 }}><Icon size={13} /> {t.tagline}</span>
        <h1 className="text-3xl font-bold leading-tight sm:text-5xl" style={{ fontFamily: t.heading, color: c.text, textTransform: t.upper ? "uppercase" : "none", fontStyle: t.rule ? "italic" : "normal" }}>
          Confirma el pago de tu reservación
        </h1>
      </div>
    </section>
  );
};

export const PaymentTemplate = (props) => <XcaretCheckout {...props} />;

export const ClassicTemplate = ({ hotel, data, preview = false, payState = {}, onPay }) => {
  const t = THEMES[hotel.template || hotel.slug] || THEMES.riu;
  const c = t.dark
    ? { text: "#F8FAFC", muted: "rgba(248,250,252,0.6)", line: "rgba(255,255,255,0.08)", soft: "rgba(255,255,255,0.05)", card: rgba(hotel.secondary_color, 0.14), border: rgba(hotel.primary_color, 0.45) }
    : { text: "#16181D", muted: "rgba(22,24,29,0.55)", line: "rgba(0,0,0,0.07)", soft: rgba(hotel.primary_color, 0.08), card: "#FFFFFF", border: t.border > 1 ? hotel.primary_color : "rgba(0,0,0,0.08)" };
  const card = { background: c.card, borderRadius: t.radius, border: `${t.border}px solid ${c.border}`, boxShadow: t.dark ? `0 0 40px ${rgba(hotel.accent_color, 0.25)}` : t.shadow ? "0 20px 50px -24px rgba(0,0,0,0.25)" : "none" };
  const btnBg = hotel.primary_color;
  const btnText = t.dark ? "#0F0C1B" : "#FFFFFF";
  const disabled = preview || payState.disabled;
  const h2 = { fontFamily: t.heading, textTransform: t.upper ? "uppercase" : "none", letterSpacing: t.upper ? "0.06em" : 0 };

  return (
    <div className="min-h-full" style={{ background: hotel.background_color, color: c.text, fontFamily: t.body }} data-testid={`payment-template-${hotel.slug}`}>
      {preview && (
        <div className="flex items-center justify-center gap-2 bg-[#FFD84D] py-2 pl-4 pr-12 text-center text-xs font-bold uppercase tracking-wider text-[#2B2200]" data-testid="payment-preview-mode-badge">
          <Eye size={14} /> Vista previa · No se generó un enlace real ni se realizarán cargos
        </div>
      )}
      <Header hotel={hotel} c={c} t={t} />
      <Hero hotel={hotel} c={c} t={t} />
      <main className="relative mx-auto -mt-8 grid max-w-6xl gap-6 px-5 pb-16 sm:px-10 lg:grid-cols-[1.5fr_1fr]">
        <section className="p-6 sm:p-8" style={card}>
          <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: hotel.accent_color }}>Detalles de la reservación</p>
          {t.rule && <div className="mt-3 h-px w-16" style={{ background: hotel.accent_color }} />}
          <p className="mt-4 text-sm" style={{ color: c.muted }}>Huésped</p>
          <p className="text-2xl font-bold sm:text-3xl" style={h2} data-testid="pay-guest-name">{orNA(data.guest_name)}</p>
          <Guests d={data} c={c} t={t} />
          {data.property_name && <Row c={c} label="Hotel" value={data.property_name} testid="pay-property" />}
          <Row c={c} label="Habitación" value={orNA(data.room)} testid="pay-room" />
          <Row c={c} label="Check-In" value={fmtDate(data.check_in)} testid="pay-check-in" />
          <Row c={c} label="Check-Out" value={fmtDate(data.check_out)} testid="pay-check-out" />
          <Row c={c} label="Resort Credits" value={fmtCredits(data.resort_credits)} testid="pay-resort-credits" />
          <Row c={c} label="Transportación" value={orNA(data.transportation)} testid="pay-transportation" />
          <Row c={c} label="Servicios adicionales" value={orNA(data.additional_services)} testid="pay-additional-services" />
          {data.total_cents != null && <>
            <Row c={c} label="Monto total de reservación" value={fmtMoney(data.total_cents, data.currency)} testid="pay-total-amount" />
            <Row c={c} label="Pago inicial" value={fmtMoney(data.initial_cents ?? data.amount_cents, data.currency)} testid="pay-initial-payment" />
            <Row c={c} label="Saldo pendiente" value={data.balance_cents == null ? "Sin especificar" : fmtMoney(data.balance_cents, data.currency)} testid="pay-balance" />
          </>}
        </section>
        <aside className="h-fit p-6 sm:p-8 lg:sticky lg:top-6" style={card}>
          <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: c.muted }}>Total a pagar</p>
          <p className="mt-2 text-4xl font-bold sm:text-5xl" style={{ ...h2, color: t.dark ? hotel.primary_color : c.text }} data-testid="pay-amount">{fmtMoney(data.amount_cents, data.currency)}</p>
          <p className="mt-1 text-xs" style={{ color: c.muted }}>Moneda: <span data-testid="pay-currency">{data.currency || "USD"}</span> · Los Resort Credits son informativos y no se descuentan del importe.</p>
          {equivalent(data.amount_cents, data.currency, data.exchange_rate) && (
            <p className="mt-1 text-xs font-semibold" style={{ color: c.muted }} data-testid="pay-equivalent">
              Equivale a {equivalent(data.amount_cents, data.currency, data.exchange_rate)} · tipo de cambio {data.exchange_rate} MXN/USD
            </p>
          )}
          {payState.notice && (
            <div className="mt-4 flex gap-2 rounded-md px-3 py-3 text-sm" data-testid="pay-notice"
              style={{ background: payState.tone === "ok" ? "rgba(16,185,129,0.12)" : "rgba(245,158,11,0.14)", color: t.dark ? "#FDE68A" : "#7A4B00" }}>
              {payState.tone === "ok" ? <ShieldCheck size={18} className="shrink-0" /> : <AlertTriangle size={18} className="shrink-0" />}
              <span>{payState.notice}</span>
            </div>
          )}
          <button data-testid="public-pay-button" disabled={disabled} onClick={onPay}
            className="mt-6 w-full px-6 py-4 text-base font-bold transition-[transform,opacity] duration-200 enabled:hover:-translate-y-0.5 enabled:active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-50"
            style={{ background: btnBg, color: btnText, borderRadius: Math.min(t.radius, 999), fontFamily: t.heading, textTransform: t.upper ? "uppercase" : "none", letterSpacing: t.upper ? "0.08em" : 0 }}>
            {payState.loading ? "Redirigiendo…" : payState.label || "Continuar al pago"}
          </button>
          <p className="mt-4 flex items-center justify-center gap-1.5 text-center text-xs" style={{ color: c.muted }}>
            <Lock size={12} /> Serás redirigido al checkout seguro del procesador.
          </p>
        </aside>
      </main>
    </div>
  );
};
